/* global Shopify */

(function() {
  /**
   * @typedef {Object} MTProduct
   * @property {string | number} id
   * @property {string} title
   * @property {string} url
   * @property {string | null} image
   * @property {string} alt
   * @property {number | null} price
   * @property {number | null} compare_at_price
   */

  class MTRecentVisits {
    /**
     * @param {HTMLElement} section
     */
    constructor(section) {
      this.section = section;
      /** @type {HTMLElement | null} */
      this.grid = section.querySelector('.mt-related-product__grid');
      /** @type {HTMLElement | null} */
      this.prevBtn = section.querySelector('.mt-related-product__arrow--prev');
      /** @type {HTMLElement | null} */
      this.nextBtn = section.querySelector('.mt-related-product__arrow--next');
      /** @type {HTMLElement | null} */
      this.emptyState = section.querySelector('.mt-related-product__empty');
      /** @type {HTMLElement | null} */
      this.dotsContainer = section.querySelector('.mt-recent-visits__dots');
      this.limit = parseInt(section.dataset.limit || '4', 10);
      this.productId = section.dataset.productId;
      this.isMobile = window.innerWidth <= 749;
      this.mobileItemsPerPage = 2; // 移动端每页显示2个
      this.searchUrl = section.dataset.searchUrl || '/search/suggest.json';
      this.rootUrl = (section.dataset.rootUrl || window.location.origin || '').replace(/\/$/, '');
      this.moneyFormat = section.dataset.moneyFormat || window?.Shopify?.currency?.active || '';
      this.loaded = false;
      /** @type {MTProduct[]} */
      this.items = [];
      this.pointer = 0;
      this.intersectionObserver = null;
      /** @type {((event: Event) => void) | null} */
      this.scrollHandler = null;
      /** @type {((event: TouchEvent) => void) | null} */
      this.touchHandler = null;
      this.isScrolling = false; // 标记是否正在程序化滚动
      this.lastScrollLeft = 0; // 跟踪上次滚动位置

      if (!this.grid) return;

      this.bindEvents();
      this.observe();
      this.handleResize();
      window.addEventListener('resize', () => this.handleResize());
      this.bindScrollEvents();
    }

    bindScrollEvents() {
      if (!this.grid) {
        return;
      }
      
      // 移除旧的监听器（如果存在）
      if (this.scrollHandler) {
        this.grid.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
      if (this.touchHandler) {
        this.grid.removeEventListener('touchmove', this.touchHandler);
        this.touchHandler = null;
      }
      
      // 清除旧的动画帧（如果存在）
      if (this.scrollAnimationFrame) {
        cancelAnimationFrame(this.scrollAnimationFrame);
        this.scrollAnimationFrame = null;
      }
      
      // 只在移动端绑定滚动事件
      if (this.isMobile && this.grid) {
        /** @type {number | null} */
        let scrollTimeout = null;
        // 初始化 lastScrollLeft
        if (this.grid) {
          this.lastScrollLeft = this.grid.scrollLeft;
        }
        
        this.scrollHandler = () => {
          // 如果正在程序化滚动，忽略手动滚动事件
          if (this.isScrolling) {
            return;
          }
          if (!this.grid) {
            return;
          }
          
          const currentScrollLeft = this.grid.scrollLeft;
          
          // 如果滚动位置没有变化，不处理
          if (Math.abs(currentScrollLeft - this.lastScrollLeft) < 1) {
            return;
          }
          this.lastScrollLeft = currentScrollLeft;
          
          // 使用 requestAnimationFrame 确保在下一帧更新
          if (this.scrollAnimationFrame) {
            cancelAnimationFrame(this.scrollAnimationFrame);
          }
          
          this.scrollAnimationFrame = requestAnimationFrame(() => {
            if (scrollTimeout) {
              clearTimeout(scrollTimeout);
            }
            scrollTimeout = setTimeout(() => {
              this.updateDotsFromScroll();
              scrollTimeout = null;
            }, 50); // 减少延迟，更快响应
          });
        };
        
        // 绑定滚动事件
        this.grid.addEventListener('scroll', this.scrollHandler, { passive: true });
        
        // 也绑定 touchmove 事件作为备用（移动端）
        this.touchHandler = (/** @type {TouchEvent} */ e) => {
          // touch 事件会触发 scroll，所以这里只是记录
        };
        this.grid.addEventListener('touchmove', this.touchHandler, { passive: true });
      }
    }

    updateDotsFromScroll() {
      if (!this.grid || !this.isMobile) {
        return;
      }
      
      // 如果正在程序化滚动，延迟检查
      if (this.isScrolling) {
        setTimeout(() => {
          this.updateDotsFromScroll();
        }, 600);
        return;
      }
      
      const scrollLeft = this.grid.scrollLeft;
      const firstChild = /** @type {HTMLElement} */ (this.grid.children[0]);
      
      if (!firstChild || this.items.length === 0) {
        return;
      }
      
      const itemsPerPage = this.mobileItemsPerPage;
      const itemWidth = firstChild.offsetWidth + 2; // 2px gap
      const pageWidth = itemWidth * itemsPerPage; // 每页宽度（2个产品）
      
      if (pageWidth <= 0) {
        return; // 防止除零错误
      }
      
      // 计算当前页面索引
      // 使用更精确的计算方式：根据滚动位置判断当前页面
      const maxPageIndex = Math.ceil(this.items.length / itemsPerPage) - 1;
      
      // 计算原始页面索引（小数）
      const rawPageIndex = scrollLeft / pageWidth;
      
      // 使用 Math.round 来找到最接近的页面
      // 但需要添加一个偏移量，使得在滚动到页面中间时更容易切换到下一页
      // 偏移量0.3意味着滚动超过页面30%时就切换到下一页
      let currentPageIndex = Math.round(rawPageIndex + 0.2);
      
      // 确保索引在有效范围内
      currentPageIndex = Math.max(0, Math.min(currentPageIndex, maxPageIndex));
      
      // 更新 pointer 以保持一致性
      const newPointer = currentPageIndex * itemsPerPage;
      
      if (newPointer !== this.pointer) {
        this.pointer = newPointer;
        // 只更新 dot 点，不重新渲染整个列表
        this.updateDots();
      }
    }

    handleResize() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 749;
      if (wasMobile !== this.isMobile && this.loaded) {
        this.render();
      }
      // 重新绑定滚动事件（因为 isMobile 可能改变）
      this.bindScrollEvents();
    }

    bindEvents() {
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      this.updateArrows();
    }

    observe() {
      this.intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.intersectionObserver.observe(this.section);
    }

    getViewedProducts() {
      try {
        const stored = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
        if (!Array.isArray(stored)) return [];
        const ids = stored
          .map((id) => `${id}`.trim())
          // 过滤掉旧的纯数字 ID（Shopify 的 /products/{id}.js 不支持），只保留 handle
          .filter((id) => id && !/^\d+$/.test(id))
          .filter((id) => id !== `${this.productId}`);
        // 移除数量限制，获取所有浏览过的产品
        return ids;
      } catch (e) {
        return [];
      }
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      const viewedIds = this.getViewedProducts();
      if (viewedIds.length === 0) {
        this.showEmpty();
        return;
      }

      try {
        const products = await this.fetchProducts(viewedIds);
        // 移除数量限制，显示所有获取到的产品
        this.items = products;

        if (this.items.length > 0) {
          this.render();
          // 确保在数据加载后绑定滚动事件
          if (this.isMobile) {
            setTimeout(() => {
              this.bindScrollEvents();
              // 确保滚动完成后 isScrolling 被重置
              this.isScrolling = false;
            }, 600);
          }
        } else {
          this.showEmpty();
        }
      } catch (e) {
        this.showEmpty();
      }
    }

    /**
     * @param {string[]} ids
     * @returns {Promise<MTProduct[]>}
     */
    async fetchProducts(ids) {
      // 直接逐个走 product.js（使用 handle），避免 suggest 接口 422 或证书问题
      const results = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        if (!id || /^\d+$/.test(id)) {
          continue; // 保护：忽略旧的数字 ID，避免 404
        }
        const p = await this.fetchSingleProduct(id);
        if (p) {
          if (String(p.id) !== String(this.productId)) {
            results.push(p);
          }
        }
      }
      return results;
    }

    /**
     * 逐个拉取 /products/{handle or id}.js
     * @param {string} idOrHandle
     * @returns {Promise<MTProduct | null>}
     */
    async fetchSingleProduct(idOrHandle) {
      // 默认用当前站点 origin，避免预览域名与 shop 域名证书不匹配
      const base = window.location.origin || this.rootUrl || '';
      const tryUrls = [
        `${base}/products/${encodeURIComponent(idOrHandle)}.js`,
        `/products/${encodeURIComponent(idOrHandle)}.js`,
      ];

      for (const url of tryUrls) {
        try {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) continue;
          const data = await res.json();
          return this.normalizeProduct(data);
        } catch (e) {
          continue;
        }
      }
      return null;
    }

    /**
     * @param {any} p
     * @returns {MTProduct | null}
     */
    normalizeProduct(p) {
      if (!p || !p.title) return null;

      const handle = p.handle || p.product_handle;
      const url = p.url || (handle ? `${this.rootUrl}/products/${handle}` : null);
      if (!url) return null;

      const imageObj = p.featured_image || p.images?.[0] || p.image || null;
      const imageSrc = imageObj?.src || imageObj;
      const alt = imageObj?.alt || p.title;

      // Title formatting: for titles like
      // "Artzuzu Custom Portrait For Pets - The Chef"
      // we only show the part after the last " - " (e.g. "The Chef")
      let displayTitle = p.title;
      if (typeof p.title === 'string') {
        const parts = p.title.split(' - ');
        if (parts.length > 1) {
          displayTitle = parts[parts.length - 1].trim();
        }
      }

      const price = p.price ?? p.price_min ?? p?.variants?.[0]?.price ?? null;
      const compareAt =
        p.compare_at_price ??
        p.compare_at_price_min ??
        p?.variants?.[0]?.compare_at_price ??
        null;

      return {
        id: p.id,
        title: displayTitle,
        url,
        image: imageSrc || null,
        alt,
        price: this.toCents(price),
        compare_at_price: this.toCents(compareAt),
      };
    }

    /**
     * @param {any} value
     * @returns {number | null}
     */
    toCents(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        return value > 1000 ? Math.round(value) : Math.round(value * 100);
      }
      const num = parseFloat(value);
      if (Number.isNaN(num)) return null;
      return num > 1000 ? Math.round(num) : Math.round(num * 100);
    }

    next() {
      if (this.isMobile) {
        const itemsPerPage = this.mobileItemsPerPage;
        if (this.items.length <= itemsPerPage) return;
        const totalPages = Math.ceil(this.items.length / itemsPerPage);
        const maxPointer = (totalPages - 1) * itemsPerPage;
        this.pointer = Math.min(this.pointer + itemsPerPage, maxPointer);
      } else {
        if (this.items.length <= this.limit) return;
        const totalPages = Math.ceil(this.items.length / this.limit);
        const maxPointer = (totalPages - 1) * this.limit;
        this.pointer = Math.min(this.pointer + this.limit, maxPointer);
      }
      this.render();
    }

    prev() {
      if (this.isMobile) {
        const itemsPerPage = this.mobileItemsPerPage;
        if (this.items.length <= itemsPerPage) return;
        this.pointer = Math.max(this.pointer - itemsPerPage, 0);
      } else {
        if (this.items.length <= this.limit) return;
        this.pointer = Math.max(this.pointer - this.limit, 0);
      }
      this.render();
    }

    /**
     * @param {number} pageIndex
     */
    goToPage(pageIndex) {
      if (this.isMobile) {
        const itemsPerPage = this.mobileItemsPerPage;
        const maxPage = Math.ceil(this.items.length / itemsPerPage) - 1;
        const targetPage = Math.max(0, Math.min(pageIndex, maxPage));
        this.pointer = targetPage * itemsPerPage;
      } else {
        const maxPage = Math.ceil(this.items.length / this.limit) - 1;
        const targetPage = Math.max(0, Math.min(pageIndex, maxPage));
        this.pointer = targetPage * this.limit;
      }
      this.render();
    }

    render() {
      const grid = this.grid;
      if (!grid) return;
      grid.innerHTML = '';
      
      const itemsPerPage = this.isMobile ? this.mobileItemsPerPage : this.limit;
      const slice = [];
      for (let i = 0; i < Math.min(itemsPerPage, this.items.length); i += 1) {
        const idx = (this.pointer + i) % this.items.length;
        slice.push(this.items[idx]);
      }

      // 移动端显示所有项目用于横向滚动
      if (this.isMobile) {
        this.items.filter(Boolean).forEach((item, index) => {
          if (!item) return;
          const card = this.createCard(item);
          grid.appendChild(card);
        });
      } else {
        slice.filter(Boolean).forEach((item) => {
          if (!item) return;
          const card = this.createCard(item);
          grid.appendChild(card);
        });
      }

      // 移动端滚动到当前页面
      if (this.isMobile && grid.children.length > 0) {
        const currentPageIndex = Math.floor(this.pointer / this.mobileItemsPerPage);
        const firstChild = /** @type {HTMLElement} */ (grid.children[0]);
        const itemWidth = firstChild.offsetWidth + 2; // 2px gap
        const pageWidth = itemWidth * this.mobileItemsPerPage; // 每页宽度（2个产品）
        const scrollPosition = currentPageIndex * pageWidth;
        
        // 更新 lastScrollLeft 以避免误判
        this.lastScrollLeft = scrollPosition;
        
        // 标记正在程序化滚动，避免触发滚动事件更新
        this.isScrolling = true;
        grid.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
        
        // 滚动完成后重置标记
        setTimeout(() => {
          this.isScrolling = false;
          // 更新 lastScrollLeft 为实际滚动位置
          if (this.grid) {
            this.lastScrollLeft = this.grid.scrollLeft;
          }
        }, 600);
      }

      this.updateArrows();
      this.updateDots();
      
      // 在移动端渲染后重新绑定滚动事件（因为 grid 内容可能已更新）
      if (this.isMobile) {
        // 延迟绑定以确保 DOM 已更新，并且滚动动画完成
        setTimeout(() => {
          this.bindScrollEvents();
          // 确保滚动完成后 isScrolling 被重置
          this.isScrolling = false;
        }, 600);
      }
    }

    /**
     * @param {MTProduct} item
     * @returns {HTMLElement}
     */
    createCard(item) {
      const priceHtml = this.buildPrice(item.price, item.compare_at_price);
      const imageHtml = item.image
        ? `<img class="mt-product-collection__image" src="${item.image}" alt="${this.escape(
            item.alt
          )}" loading="lazy">`
        : `<div class="mt-product-collection__image" style="background:#f5f5f5;"></div>`;

      const card = document.createElement('div');
      card.className = 'mt-product-collection__item';
      card.innerHTML = `
        <a href="${item.url}" class="mt-product-collection__product-link">
          <div class="mt-product-collection__image-wrapper">
            ${imageHtml}
          </div>
          <div class="mt-product-collection__info">
            <h3 class="mt-product-collection__product-title">${this.escape(item.title)}</h3>
            ${priceHtml}
            <button type="button" class="mt-product-collection__button">Shop Now</button>
          </div>
        </a>
      `;
      const btn = card.querySelector('.mt-product-collection__button');
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          window.location.href = item.url;
        });
      }
      return card;
    }

    /**
     * @param {number | null} price
     * @param {number | null} compareAt
     */
    buildPrice(price, compareAt) {
      const formattedPrice = this.formatMoney(price);
      const hasSale =
        price !== null && compareAt !== null && compareAt !== undefined && compareAt > price;
      if (hasSale) {
        return `
          <div class="mt-product-collection__prices">
            <span class="mt-product-collection__price mt-product-collection__price--compare">${this.formatMoney(
              compareAt
            )}</span>
            <span class="mt-product-collection__price mt-product-collection__price--sale">${formattedPrice}</span>
          </div>
        `;
      }
      return `
        <div class="mt-product-collection__prices">
          <span class="mt-product-collection__price">${formattedPrice}</span>
        </div>
      `;
    }

    /**
     * @param {number | null} cents
     */
    formatMoney(cents) {
      if (cents === null || cents === undefined) return '';
      const value = typeof cents === 'number' ? Math.trunc(cents) : parseInt(String(cents), 10);
      if (Number.isNaN(value)) return '';
      const shopify = /** @type {any} */ (window.Shopify);
      if (shopify && typeof shopify.formatMoney === 'function') {
        return shopify.formatMoney(value);
      }
      const number = (value / 100).toFixed(2);
      return this.moneyFormat ? `${this.moneyFormat} ${number}` : `$${number}`;
    }

    /**
     * @param {string} str
     */
    escape(str) {
      if (!str) return '';
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    showEmpty() {
      // 没有浏览记录时隐藏整个组件
      this.section.style.display = 'none';
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;

      const total = this.items.length;
      const itemsPerPage = this.isMobile ? this.mobileItemsPerPage : this.limit;
      
      if (total === 0 || itemsPerPage <= 0) {
        this.prevBtn.style.opacity = '0.7';
        this.nextBtn.style.opacity = '0.7';
        return;
      }

      if (total <= itemsPerPage) {
        this.prevBtn.style.opacity = '0.7';
        this.nextBtn.style.opacity = '0.7';
        return;
      }

      const totalPages = Math.ceil(total / itemsPerPage);
      const currentPage = Math.floor(this.pointer / itemsPerPage);

      this.prevBtn.style.opacity = currentPage === 0 ? '0.7' : '1';
      this.nextBtn.style.opacity = currentPage >= totalPages - 1 ? '0.7' : '1';
    }

    updateDots() {
      if (!this.dotsContainer || !this.isMobile) {
        return;
      }
      
      const total = this.items.length;
      const itemsPerPage = this.mobileItemsPerPage;
      const totalPages = Math.ceil(total / itemsPerPage);
      const currentPage = Math.floor(this.pointer / itemsPerPage);
      
      if (totalPages <= 1) {
        this.dotsContainer.innerHTML = '';
        return;
      }

      const existingDots = this.dotsContainer.querySelectorAll('.mt-recent-visits__dot');
      
      // 如果 dot 数量不匹配，重新创建
      if (existingDots.length !== totalPages) {
        this.dotsContainer.innerHTML = '';
        for (let i = 0; i < totalPages; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'mt-recent-visits__dot';
          if (i === currentPage) {
            dot.classList.add('is-active');
          }
          dot.setAttribute('aria-label', `Go to page ${i + 1}`);
          dot.addEventListener('click', () => {
            this.goToPage(i);
          });
          this.dotsContainer.appendChild(dot);
        }
      } else {
        // 只更新 is-active 类，保持动画连续性
        existingDots.forEach((dot, i) => {
          if (i === currentPage) {
            dot.classList.add('is-active');
          } else {
            dot.classList.remove('is-active');
          }
        });
      }
    }
  }

  function init() {
    const sections = document.querySelectorAll('[data-mt-recent-visits]');
    sections.forEach((section) => new MTRecentVisits(/** @type {HTMLElement} */ (section)));
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init();
  }
})();


